import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, Card, EmptyState, Field, PageBody, PageHeader, Select, StackedList, Well } from "../ui/Field";

type Member = { id: string; email: string; display_name: string | null; role: string; created_at: number };

export default function Members() {
  const { slug } = useParams();
  const [items, setItems] = useState<Member[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"editor" | "admin" | "viewer">("editor");
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const load = () => api.get<{ items: Member[] }>(`/api/sites/${slug}/members`).then((r) => setItems(r.items)).catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug) load(); }, [slug]);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setInviteUrl(null);
    try {
      const r = await api.post<{ accept_url: string }>(`/api/invites`, { email: inviteEmail, role: inviteRole });
      setInviteUrl(r.accept_url);
      setInviteEmail("");
    } catch (e) { setErr((e as Error).message); }
  }

  async function setRole(userId: string, role: string) {
    try { await api.put(`/api/sites/${slug}/members`, { user_id: userId, role }); load(); }
    catch (e) { setErr((e as Error).message); }
  }
  async function remove(userId: string) {
    if (!confirm("Remove this member?")) return;
    try { await api.delete(`/api/sites/${slug}/members/${userId}`); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 09 · Members"
        title={<>Team <em>access</em></>}
        description="Invite collaborators and set per-site roles. Membership is enforced at every API endpoint."
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="M1" title="Invite" description="The recipient registers a passkey from the link to accept.">
        <form onSubmit={invite} className="grid grid-cols-1 md:grid-cols-[1fr_180px_120px] gap-3 items-end">
          <Field label="Email" type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="mb-0" />
          <Select label="Role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as typeof inviteRole)} className="mb-0">
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
            <option value="admin">Admin</option>
          </Select>
          <Button type="submit" className="h-9 mb-0">Send invite</Button>
        </form>
        {inviteUrl && (
          <Well className="mt-4">
            <div className="label-tracked mb-1.5">Invite URL — share manually if Resend isn't configured</div>
            <code className="font-mono text-[12px] text-[var(--color-accent)] break-all">{inviteUrl}</code>
          </Well>
        )}
      </Card>

      <Card number="M2" title={`Members (${items.length})`} className="mt-6" bodyClassName="p-0">
        {items.length === 0 ? (
          <div className="p-6"><EmptyState title="No members yet." description="Invite someone using the form above." /></div>
        ) : (
          <StackedList className="px-5">
            {items.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-4 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[var(--color-paper-2)] border border-[var(--color-rule)] flex items-center justify-center display italic text-[15px] text-[var(--color-ink-2)] shrink-0">
                    {m.email.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13.5px] text-[var(--color-ink)] truncate">{m.display_name || m.email}</div>
                    <div className="text-[11.5px] text-[var(--color-muted)] truncate font-mono">{m.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge tone={m.role === "owner" ? "accent" : "neutral"}>{m.role}</Badge>
                  <select value={m.role} onChange={(e) => setRole(m.id, e.target.value)} className="h-7 px-2 text-[12px] font-mono bg-transparent border border-[var(--color-rule)] rounded-[3px] hover:border-[var(--color-rule-strong)] cursor-pointer">
                    <option value="viewer">viewer</option>
                    <option value="editor">editor</option>
                    <option value="admin">admin</option>
                    <option value="owner">owner</option>
                  </select>
                  <Button variant="ghost" size="sm" onClick={() => remove(m.id)}>Remove</Button>
                </div>
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}
